/**
 * Pure validation helpers for the shared lead field mapping table.
 *
 * Extracted from `EnhancedImportWizard.tsx` so the same rules can be reused by
 * the Campaign editor (see quantix-api/docs/campaigns/TASKS.md, task W3) without
 * forking the logic. Behaviour matches the original inline implementation
 * exactly for the import wizard's call sites (empty `combinableTargets`).
 */

/** Sentinel value used by the mapping selects to mean "don't map this field". */
export const NO_MAPPING_VALUE = '__no_mapping__';

/** Source field key -> target lead field name (or null/unmapped). */
export type LeadFieldMapping = Record<string, string | null>;

/** Shape of a single entry in the `available_fields` record returned by the API. */
export interface AvailableFieldConfig {
    label: string;
    required?: boolean;
    type?: string;
    options?: Array<string | { key: string; label: string }> | null;
    description?: string;
    // Present for custom attribute targets (custom_<name>), plus any other
    // metadata the API includes (max_length, validation_rules, ...).
    [key: string]: unknown;
}

export type AvailableFields = Record<string, AvailableFieldConfig>;

export interface MappingValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    missingRequired: string[];
}

/**
 * Whether `targetField` is already mapped from a source field other than
 * `currentSourceKey`. Targets listed in `combinableTargets` (e.g. `name`,
 * `notes` for the campaign editor's combine feature) are never reported as
 * already mapped. Defaults to `[]` so existing (non-combinable) callers keep
 * their current "a target may be used once" behaviour.
 */
export function isTargetAlreadyMapped(
    mapping: LeadFieldMapping,
    targetField: string | null | undefined,
    currentSourceKey: string,
    combinableTargets: string[] = []
): boolean {
    if (!targetField || targetField === NO_MAPPING_VALUE) return false;
    if (combinableTargets.includes(targetField)) return false;
    return Object.entries(mapping).some(
        ([sourceKey, mappedField]) => sourceKey !== currentSourceKey && mappedField === targetField
    );
}

/**
 * Checks that every field in `requiredFields` has at least one source mapped
 * to it. Mirrors the import wizard's original required-field check.
 */
export function checkRequiredFieldsMapped(
    mapping: LeadFieldMapping,
    availableFields: AvailableFields,
    requiredFields: string[]
): { missingRequired: string[]; errors: string[] } {
    const missingRequired: string[] = [];
    const errors: string[] = [];

    requiredFields.forEach(requiredField => {
        const isMapped = Object.entries(mapping).some(
            ([, field]) => field === requiredField && field !== NO_MAPPING_VALUE
        );
        if (!isMapped) {
            const fieldLabel = availableFields[requiredField]?.label || requiredField;
            missingRequired.push(fieldLabel);
            errors.push(`Required field '${fieldLabel}' must be mapped`);
        }
    });

    return { missingRequired, errors };
}

/**
 * Checks that no target field is used by more than one source field, except
 * targets listed in `combinableTargets`. Mirrors the import wizard's original
 * duplicate-mapping check when `combinableTargets` is empty (the default).
 */
export function checkDuplicateTargetMappings(
    mapping: LeadFieldMapping,
    availableFields: AvailableFields,
    combinableTargets: string[] = []
): string[] {
    const errors: string[] = [];
    const fieldCounts: Record<string, number> = {};

    Object.entries(mapping).forEach(([, field]) => {
        if (field && field !== NO_MAPPING_VALUE) {
            fieldCounts[field] = (fieldCounts[field] || 0) + 1;
        }
    });

    Object.entries(fieldCounts).forEach(([field, count]) => {
        if (count > 1 && !combinableTargets.includes(field)) {
            const fieldLabel = availableFields[field]?.label || field;
            errors.push(`Field '${fieldLabel}' is mapped to multiple columns`);
        }
    });

    return errors;
}

/**
 * Composed validation matching the import wizard's original `validation`
 * memo: required-field check + duplicate-target check + a "mapped very few
 * fields" warning based on how many of the available source fields got
 * mapped.
 */
export function validateLeadFieldMapping(
    mapping: LeadFieldMapping,
    availableFields: AvailableFields,
    requiredFields: string[],
    sourceFieldsCount: number,
    combinableTargets: string[] = []
): MappingValidationResult {
    const { missingRequired, errors: requiredErrors } = checkRequiredFieldsMapped(
        mapping,
        availableFields,
        requiredFields
    );
    const duplicateErrors = checkDuplicateTargetMappings(
        mapping,
        availableFields,
        combinableTargets
    );
    const errors = [...requiredErrors, ...duplicateErrors];
    const warnings: string[] = [];

    const mappedFieldsCount = Object.values(mapping).filter(
        field => field && field !== NO_MAPPING_VALUE
    ).length;

    if (sourceFieldsCount > 0 && mappedFieldsCount < sourceFieldsCount * 0.3) {
        warnings.push(
            'You have mapped very few columns. Consider mapping more fields for better data import.'
        );
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        missingRequired,
    };
}
